import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, Routes } from '@angular/router';

import { routes } from './app-routing.module';
import { RestService } from '@services/rest/rest.service';
import { MatSidenav } from '@angular/material/sidenav';

import { Models } from '@ibexfusion/models';
import { MessengerService } from '@services/messenger/messenger.service';
import { PrivilegeGuard } from './guards/privilege.guard';
import { driver } from 'driver.js';
import * as _ from 'lodash';

import { TranslateService } from '@services/translate/translate.service';
import { OpenReadmeService } from '@services/readme/open-readme.service';
import { environment } from '../environments/environment';
import { PackageService } from '@services/packages/package.service';
import { IdleService } from '@services/idle/idle.service';
import { DialogService } from '@services/dialog/dialog.service';
import { IntervalService } from '@services/interval/interval.service';
import { DriverRouteService } from '@services/driver-route/driver-route.service';
import { DriverRouteExecutionService } from '@services/driver-route-execution/driver-route-execution.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { IbexSnackbarComponent } from './components/ibex-snackbar/ibex-snackbar.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [PackageService, DriverRouteService, DriverRouteExecutionService],
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {

  dashboardRoute;
  title = 'Ibexfusion';
  profileName: string;
  selectedEnvironment: any;
  changeApi = environment.changeApi;
  realEnvironment = environment.name;
  availableApis = ['local', 'live', 'staging'];

  navigationItems;

  profile: Models.Profile;
  disableMyProfile = false;

  endUrl;
  generalTr;
  logoutTimer;
  isLoading = true;

  driverStartCount = 0;

  private iconsByPath = { 'dashboard': { type: 'icon', name: 'dashboard' }, 'analytics': { type: 'icon', name: 'equalizer' } };

  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('rightsidebar') rightsidebar: MatSidenav;
  @ViewChild(IbexSnackbarComponent) snackbar!: IbexSnackbarComponent;

  constructor(
    public restService: RestService, public ms: MessengerService, private router: Router, private translate: TranslateService,
    private activatedRoute: ActivatedRoute, private readme: OpenReadmeService, private priv: PrivilegeGuard, private packageService: PackageService,
    private idle: IdleService, private dialog: DialogService, private driverService: DriverRouteService, private driverRouteExecutionService: DriverRouteExecutionService,
    private snackbarService: SnackbarService
  ) {
    this.selectedEnvironment = this.ms.getActiveAPI();

    this.idle.$idle.subscribe((count) => {
      if (count) {
        this.dialog.autologout(this.generalTr.inactivityLogout, this.generalTr.logout, [this.generalTr.continue, this.generalTr.logout]).then(res => {
          if (res && res === 'logout') {
            clearTimeout(this.logoutTimer);
            this.dialog.closeAll();
            this.logout();
          } else {
            this.idle.restartTimeout();
            clearTimeout(this.logoutTimer);
          }
          document.title = 'Ibexfusion';
          IntervalService.clear('autologout');
          this.changeFavicon('/assets/favicon-new.png');
        });
        this.logoutTimer = setTimeout(() => {
          this.dialog.closeAll();
          this.logout(true);
        }, 30000);
      }
    });
  }

  async initDriver() {
    const drivers = await this.initDriverData();
    if (drivers.data && drivers.data.find(el => this.router.url.includes(el.path))) {
      this.startDriver(drivers.data.find(el => this.router.url.includes(el.path)));
    }
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.initDriverData().then(currentDrivers => {
          if (currentDrivers.data && currentDrivers.data.find(el => event.url.includes(el.path))) {
            this.startDriver(currentDrivers.data.find(el => event.url.includes(el.path)));
          }
        });
      }
    });
  }

  async initDriverData() {
    const drivers = await this.driverService.find(0, 100, {});
    const driverExecutions = await this.driverRouteExecutionService.find(0, drivers?.data?.length, {
      driverId: { $in: drivers.data.map(el => el._id) }, profileId: this.profile.ProfileID
    });
    drivers.data = drivers.data.filter(d => !driverExecutions?.data.find(de => d._id === de.driverId));
    drivers?.data?.forEach(driver => {
      driver?.steps?.forEach(el => {
        el.description = el?.description?.replace(/&nbsp;/gm, ' ')
      });
    });
    return drivers;
  }

  startDriver(driverData) {
    if (!document.querySelector(driverData.steps[0].element)) {
      this.driverStartCount++;
      return this.driverStartCount > 20 ? this.driverStartCount++ : setTimeout(() => this.startDriver(driverData), 100);
    }
    this.driverStartCount = 0;
    const drive = driver({
      animate: true, showProgress: true, steps: driverData.steps.filter(s => !!document.querySelector(s.element)).map(el => {
        return { element: el.element, popover: { title: el.title, description: el.description } }
      }),
      nextBtnText: 'Weiter', prevBtnText: 'Zurück', doneBtnText: 'Fertig', progressText: '{{current}} von {{total}}',
      disableActiveInteraction: true,
      onCloseClick: () => {
        this.setDriverExecuted(driverData._id);
        drive.destroy();
      },
      onNextClick: () => {
        if (!drive.hasNextStep()) {
          this.setDriverExecuted(driverData._id);
        }
        drive.moveNext();
      }
    });
    drive.drive();
  }

  async setDriverExecuted(id) {
    const execution = await this.driverRouteExecutionService.find(0, 1, { driverId: id, profileId: this.profile.ProfileID });
    if (!execution?.data?.length) {
      await this.driverRouteExecutionService.create({ driverId: id, profileId: this.profile.ProfileID });
    }
  }

  ngAfterViewInit() {
    let int = setInterval(() => {
      if (this.snackbar) {
        this.snackbarService.register(this.snackbar);
        clearInterval(int);
      }
    }, 100);
  }

  async ngOnInit() {
    await this.translate.loadTranslation('de');
    this.generalTr = this.translate.get('general');
    this.ms.$activeOrganisation.subscribe(() => {
      this.ms.setPackageConfiguration(null);
    });
    this.ms.$activePrivileges.subscribe(personalPrivileges => {
      if (personalPrivileges) {
        setTimeout(() => this.initDriver(), 1000);
      }
      this.getNavigation(personalPrivileges).then(items => {
        const routesWithData = this.addIcons(items);
        for (let i = 0; i < routesWithData.length; i++) {
          if (routesWithData[i].path === 'dashboard') {
            this.dashboardRoute = _.cloneDeep(routesWithData[i]);
            routesWithData.splice(i, 1);
            break;
          }
        }
        this.navigationItems = routesWithData;
        if (this.endUrl) {
          const urlArr = this.endUrl.split('/');
          let mainUrl = urlArr[1] ? urlArr[1] : '';
          if (!!mainUrl.match(/org:/)) {
            const subArr = mainUrl.split('(org:');
            mainUrl = subArr[0]
            this.navigationItems.forEach(el => {
              if (el.subRoutes) {
                el.subRoutes.forEach(sr => {
                  if (sr.path === mainUrl) {
                    this.checkActivatedRoute({ path: mainUrl, }, el.data.title);
                  }
                })
              }
            });
          } else if (urlArr[2] && !!urlArr[2].match(/org:/)) {
            const subArr = urlArr[2].split('(org:');
            const packageId = subArr[0];
            this.navigationItems.forEach(el => {
              if (el.subRoutes) {
                el.subRoutes.forEach(sr => {
                  if (sr.path === mainUrl && sr.data && sr.data.packageId === packageId) {
                    this.checkActivatedRoute({ path: mainUrl, data: { packageId: packageId }, setButton: true }, el.data.title);
                  }
                })
              }
            });
          }
        }
      });
      this.disableMyProfile = this.priv.checkIfPrivilege('noWrite', 'my-profile');
    });
    this.ms.$activeProfile.subscribe(profile => {
      if (profile) {
        this.profile = profile;
        if (profile.nickName) {
          this.profileName = profile.nickName;
        } else if (profile.firstName && profile.lastName) {
          this.profileName = profile.firstName + ' ' + profile.lastName;
        } else if (profile.email) {
          this.profileName = profile.email;
        }
      }
    });
    this.ms.$routeFromDashboard.subscribe(route => {
      if (route) {
        this.checkActivatedRoute(route, route.mainDataTitle);
      }
    })
    this.readme.$openReadMe.subscribe(value => {
      if (value !== null) {
        this.toggleReadme(value);
      }
    });

    this.ms.initialize().then(() => {
      this.isLoading = false;
    });
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (event.url !== '/' && !!event.url.match(/returnUrl/)) {
          this.endUrl = event.url;
        }
      }
    });
  }

  changeFavicon(url) {
    let link: any = document.querySelector('link[rel~="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  }

  public async getNavigation(privileges: Models.MyPrivilege[]): Promise<Routes> {
    const currentOrg = this.ms.$activeOrganisation.getValue();
    const customerId = currentOrg?.ibex3customerId || currentOrg?.organisationCustomerId;
    const packages = await this.getPackages(customerId, currentOrg ? currentOrg.OrganisationID : null);
    if (customerId && packages?.length) {
      let navigationItems = [];
      navigationItems.push({ path: 'dashboard' });
      packages.sort((a, b) => {
        return !!a.title.match(/master/i) ? -1 : 1;
      });
      for (const pak of packages) {
        const isScreen = !!pak.isScreenPackage;
        const isMaster = !!pak.isMasterPackage;
        const isIbexsmart = !!pak?.devices?.length ? pak.devices.includes('ibexsmart') : !!pak.ibexApp?.enabled;
        const isIbextouch = !!pak?.devices?.length ? pak.devices.includes('ibextouch') : !!pak.ibexTouch?.enabled;
        const isIbexinfo = !!pak?.devices?.length ? pak.devices.includes('ibexinfo') : !!pak.ibexInfo?.enabled;
        const isIbextablet = !!pak?.devices?.length ? pak.devices.includes('ibextablet') : !!pak.ibexTablet?.enabled;
        const isIbextv = !!pak?.devices?.length ? pak.devices.includes('ibextv') : !!pak.ibexTV?.enabled;
        const isTweebie = !isScreen && !isMaster && !isIbexsmart && !isIbextouch;
        const subRoutes: any[] = ['dynamic-content', 'resources', 'contact', 'navigation', 'images', 'legal', 'notifications'].map(r => _.cloneDeep(this.getRouteByPath(r)));
        if (isIbexsmart) {
          subRoutes.push(_.cloneDeep(this.getRouteByPath('my-users')));
        }
        for (let i = 0; i < subRoutes.length; i++) {
          subRoutes[i].metaConfig = {
            isDestination: pak.tweebie.isDestination, isScreenPackage: pak.isScreenPackage,
            isMasterPackage: pak.isMasterPackage || pak.title.toLowerCase().indexOf('master') > -1,
            isFree: pak.tweebie.isFree, underConstruction: pak.underConstruction, linkName: pak.linkName, noTouch2015: pak.isNotTouch2015ScreenPackage,
            screenUuid: pak?.screenUuids?.length ? pak.screenUuids[0] : '', enableIbexApp: pak?.ibexApp?.enabled,
            enableIbexTouch: pak?.ibexTouch?.enabled, destinationId: pak.tweebie.destinationId, isPortrait: pak?.ibexTouch?.isPortrait, enableIbexInfo: pak?.ibexInfo?.enabled,
            enableIbexTablet: pak?.ibexTablet?.enabled, enableIbexTV: pak?.ibexTV?.enabled, raw: pak
          }
          if (!PrivilegeGuard.checkPrivileges(privileges, subRoutes[i].data.privileges)) {
            subRoutes.splice(i, 1);
            i--;
            continue;
          }
          if (subRoutes[i].path !== 'contact') {
            subRoutes[i].data['packageId'] = pak._id;
          }
        }
        let title = pak.title;
        const isMasterPackage = !!title.match(/master/i);
        title = title.replace(/(\*A|\*W):/gi, 'tweebie').replace(/\*S:/gi, 'ibextouch').replace(/(Premium\s)?Touch(\s)?([S|s]creen)?:?/, isMasterPackage ? '' : 'ibextouch');
        if (!!title.match(/master/i)) {
          title = 'Master ' + title.replace(/Master/i, '');
        }
        if (!title.match(/(ibex?(touch|smart|info|tablet)|tweebie|master)/i)) {
          title = 'tweebie ' + title;
        }
        title = title.replace(/tweebie[\s]|^ibex\w*[\s]/, '');
        navigationItems.push({
          data: {
            title, isIbexsmart, isIbextouch, isIbexinfo, isTweebie, isMasterPackage, isIbextablet, isIbextv,
            displayOptions: { hideWhenDashboard: true }, screenUuid: !!pak.screenUuids && pak.screenUuids.length > 0
          },
          subRoutes, noTranslation: true, path: ''
        });
      }
      this.ms.setDataForDashboard(navigationItems);
      const routesToSkip = ['dynamic-content', 'legal', 'images', 'resources', 'my-users', 'contact', 'apps', 'navigation', 'my-packages', 'notifications'];
      const additionalRoutes = await this.getNavigationByPrivilegeOnly(privileges, customerId, packages, routesToSkip);
      navigationItems = navigationItems.concat(additionalRoutes);
      return navigationItems as any;
    } else {
      this.ms.setDataForDashboard(null);
    }

    return this.getNavigationByPrivilegeOnly(privileges, customerId, packages);
  }

  private getNavigationByPrivilegeOnly(privileges: Models.MyPrivilege[], customerId, packages: Models.Package[], skipRoutes = []) {
    let routesWithData: Routes = [];
    const hideRoutings = _.filter(privileges, { 'privilegeName': 'hideRouting' });
    for (const route of _.filter(routes, a => {
      return skipRoutes.indexOf(a.path) === -1
    })) {
      let showRouting = true;
      if (!route.data || !route.data.title || !privileges || (!PrivilegeGuard.checkPrivileges(privileges, route.data.privileges) && !route.data.allowAll)) {
        showRouting = false;
      } else if (hideRoutings.length && route.data.privileges && route.data.privileges.length) {
        route.data.privileges.map(privilege => {
          if (_.find(hideRoutings, item => {
            return item.value === privilege.value;
          })) {
            showRouting = false;
          }
        });
        if (hideRoutings.find(el => el.value === route.path)) {
          showRouting = false;
        }
      }
      if (showRouting) {
        routesWithData.push(route);
      }
    }
    const dashboardIndex = _.findIndex(routesWithData, { 'path': 'dashboard' });
    if (dashboardIndex > -1) {
      this.dashboardRoute = _.cloneDeep(routesWithData[dashboardIndex]);
      routesWithData.splice(dashboardIndex, 1);
    }
    if (!(customerId && packages?.length)) {
      routesWithData = _.orderBy(routesWithData, ['data.title']);
    }
    return Promise.resolve(routesWithData);
  }

  public changeEnvironment(env): void {
    this.ms.changeActiveApi(env, true);
  }

  public logout(auto = false): void {
    document.title = 'Ibexfusion';
    IntervalService.clear('autologout');
    this.changeFavicon('/assets/favicon-new.png');
    this.ms.logout().then(() => {
      this.router.navigateByUrl('(auth:auth/(authentication:message))?messagetype=logout', { replaceUrl: true });
      this.idle.clearTimeout();
    });
    if (auto) {
      this.router.navigateByUrl('(auth:auth/(authentication:message))?messagetype=autologout', { replaceUrl: true });
    }
  }

  public navigateToProfile(): void {
    this.router.navigate(['my-profile']);
  }

  public navigateToOrganization(): void {
    this.router.navigate(['my-organization']);
  }

  public toggleSidenav(): void {
    this.sidenav.toggle();
  }

  public toggleReadme(value?) {
    if (value) {
      this.rightsidebar.open();
    } else if (value === false) {
      this.rightsidebar.close();
    } else {
      this.rightsidebar.toggle();
    }
  }

  public clearOrganisation() {
    this.ms.setOrganisation(null);
    this.router.navigateByUrl('selectOrganisation');
  }

  navigateToPage(route) {
    this.checkActivatedRoute(route);
    this.ms.setPackageConfiguration(null);
    this.router.navigate([route?.path]);
  }

  navigateToSubPage(route, mainRouteTitle) {
    const commands: any = [{ outlets: { primary: [route?.path], org: this.ms.getOrganisation().name } }];
    if (route?.data?.packageId) {
      commands[0].outlets.primary.push(route.data.packageId);
    }
    if (route?.path === 'resources') {
      commands[0].outlets.primary.push('media');
    }
    if (route && route?.metaConfig) {
      this.ms.setPackageConfiguration(route.metaConfig);
    } else {
      this.ms.setPackageConfiguration(null);
    }
    this.checkActivatedRoute(route, mainRouteTitle);
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => this.router.navigate(commands));
  }

  checkActivatedRoute(route, mainRouteTitle = '') {
    this.navigationItems.forEach(el => {
      if (el.subRoutes && el.subRoutes.length) {
        el.subRoutes.forEach(subRoute => {
          subRoute.activated = false;
          if (route.path === subRoute.path && el.data.title === mainRouteTitle) {
            if (route.data && route.data.packageId && route.data.packageId === subRoute.data.packageId) {
              subRoute.activated = true;
              el.open = true;
              if (route.setButton && subRoute.metaConfig) {
                this.ms.setPackageConfiguration(subRoute.metaConfig);
              }
            } else if (!route.data || !route.data.packageId) {
              subRoute.activated = true;
              el.open = true;
            }
          }
        });
      }
    });
  }

  getRouteByPath(pathName) {
    return routes.find(r => r.path === pathName);
  }

  private async getPackages(customerId, organisationId) {
    if (!customerId) {
      return Promise.resolve(null);
    }
    let query = <any>{ customerId: customerId, underConstruction: { $ne: true } }

    if (this.ms.getOrganisation().ParentOrganisationID) {
      query = { OwnerOrganisationID: this.ms.getOrganisation().OrganisationID, underConstruction: { $ne: true } };
    }
    const res = [];
    try {
      const result = await this.packageService.find(0, 20, query);
      for (const pak of result?.data) {
        if (pak && pak.OwnerOrganisationID) {
          if (pak.OwnerOrganisationID === organisationId) {
            res.push(pak);
          }
        } else if (pak) {
          res.push(pak);
        }
      }
    } catch (err) {
      console.warn(err);
    }
    return res;
  }

  addIcons(items: any[]) {
    return items.map(item => {
      if (item.path && item.path.length) {
        item['icon'] = this.iconsByPath[item.path];
      } else {
        let url = '/assets/img/single-logos/';
        if (item.data.isTweebie) {
          url += 'tweebie.png';
        } else {
          url += 'ibex-' + (item.data.isMasterPackage ? 'master' : (item.data.isIbexinfo ? 'info' :
            (item.data.isIbextouch || item.data.screenUuid ? 'touch' : (item.data.isIbextablet ? 'tablet' : 'smart')))) + '.png';
        }
        item['icon'] = { type: 'logo', url }
      }
      return item;
    });
  }

  public isVisible(displayOptions) {
    return displayOptions.hideWhenDashboard ? !this.router.url.match(/dashboard/) : !!this.router.url.match(/dashboard/);
  }
}
